import { useMemo, useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useQueryState, parseAsBoolean, parseAsString } from 'nuqs';
import { toast } from 'sonner';
import { AgentStreamView } from '@/components/agent/AgentStreamView';
import { LaunchAgentDialog } from '@/components/agent/launch-agent-dialog';
import { DeleteAgentDialog } from '@/components/agent/agent-actions/DeleteAgentDialog';
import { TwoPanelLayout } from '@reflex/ui/components/layout/TwoPanelLayout';
import { ConfirmDialog } from '@reflex/ui/components/shared/ConfirmDialog';
import { useArchiveAgent } from '@/hooks/agents/useAgents';
import { useLiveAgentTurnSignals } from '@/hooks/agents/useLiveAgentTurnSignals';
import { activityFromDurableAgentState } from '@/hooks/agents/agent-activity-utils';
import { useTwoPanelState } from '@/hooks/ui/useTwoPanelState';
import { shouldConfirmArchive } from '@/lib/status-colors';
import { fadeInUp } from '@/lib/motion';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { buildBuckets, itemId, type InboxItem, type Selection, type Buckets } from './inbox-utils';
import { InboxSidebar, type InboxTakeoverChrome } from './InboxSidebar';
import { useTakeoverEscape } from '@/components/layout/TakeoverShell';

const noopBack = () => {};
import { EmptyInbox } from './EmptyInbox';
import { FlowRunDetail } from '@reflex/plugin-flows/web/components';
import { NotificationDetailPanel } from '@reflex/plugin-notifications/web/components';
import { useNotifications } from '@reflex/plugin-notifications/web/useNotifications';
import { DecisionGateDetail } from './DecisionGateDetail';
import type { DecisionGate } from '@/hooks/useDecisionGates';
import type { Agent, FlowRunSummary } from '@/lib/types';

function selectionKey(sel: Selection): string {
  switch (sel.type) {
    case 'agent':
      return `a:${sel.agent.id}`;
    case 'decision-gate':
      return `g:${sel.gateId}`;
    case 'flow-run':
      return `f:${sel.runId}`;
    case 'notification':
      return `n:${sel.notificationId}`;
  }
}

function renderFlowRunBranch({
  runId,
  flowRuns,
  renderFlowRunDetail,
  onToggleSidebar,
}: {
  runId: string;
  flowRuns: FlowRunSummary[];
  renderFlowRunDetail?: (run: FlowRunSummary, onToggleSidebar: () => void) => React.ReactNode;
  onToggleSidebar: () => void;
}) {
  if (renderFlowRunDetail) {
    const matchedRun = flowRuns.find((r) => r.id === runId);
    if (matchedRun) {
      return renderFlowRunDetail(matchedRun, onToggleSidebar);
    }
  }
  return <FlowRunDetail key={runId} runId={runId} onToggleSidebar={onToggleSidebar} />;
}

export interface InboxViewProps {
  agents: Agent[];
  flowRuns: FlowRunSummary[];
  /** Pending decision gates (the neutral replacement for flow approvals). */
  gates?: DecisionGate[];
  /**
   * Optional custom renderer for agent detail. When provided, replaces
   * the default AgentStreamView. Used by simulation mode to render
   * EmbeddableReplay instead of live stream data.
   */
  renderAgentDetail?: (agent: Agent, onToggleSidebar: () => void) => React.ReactNode;
  /**
   * Optional custom renderer for flow-run detail. When provided, replaces
   * the default `FlowRunDetail`. Used by demo simulation mode to
   * render a fixture approval card without hitting the flows API.
   */
  renderFlowRunDetail?: (run: FlowRunSummary, onToggleSidebar: () => void) => React.ReactNode;
  /**
   * Optional custom renderer for decision-gate detail. When provided,
   * replaces the default `DecisionGateDetail`. Used by demo simulation mode
   * to render a fixture approval card without hitting the decision-gate API.
   */
  renderDecisionGateDetail?: (gate: DecisionGate, onToggleSidebar: () => void) => React.ReactNode;
  /**
   * Override the TwoPanelLayout autoSaveId. Defaults to 'inbox-sidebar'.
   * Use a distinct key for fixture/demo contexts to avoid width conflicts.
   */
  autoSaveId?: string;
  /**
   * Whether to show the new-agent launcher button. Defaults to true.
   * Set to false in fixture/demo contexts where launching is disabled.
   */
  showLaunchButton?: boolean;
  /**
   * Takeover chrome for the routed inbox surface: the desktop sidebar opens
   * with the shared back chip + identity header, and `esc` restores the
   * last work location. Absent in demo/fixture embeds, which keep the
   * plain panel header.
   */
  takeover?: InboxTakeoverChrome;
}

export function InboxView({
  agents,
  flowRuns,
  gates = [],
  renderAgentDetail,
  renderFlowRunDetail,
  renderDecisionGateDetail,
  autoSaveId = 'inbox-sidebar',
  showLaunchButton = true,
  takeover,
}: InboxViewProps) {
  const [selectionParam, setSelectionParam] = useQueryState('item', parseAsString);
  const panel = useTwoPanelState();
  // `esc` mirrors the back chip whenever the takeover chrome is present.
  useTakeoverEscape(takeover?.onBack ?? noopBack, takeover !== undefined);
  const navigate = useNavigate();
  const routes = useOrgRoutes();

  const [newParam, setNewParam] = useQueryState('new', parseAsBoolean);
  const launchDialogOpen = !!newParam;
  const setLaunchDialogOpen = useCallback(
    (open: boolean) => {
      void setNewParam(open ? true : null, { history: 'replace' });
    },
    [setNewParam],
  );
  const handleOpenLaunchDialog = useCallback(
    () => setLaunchDialogOpen(true),
    [setLaunchDialogOpen],
  );
  // The inbox only surfaces agents that need attention (needs_input,
  // interrupted, idle, error, completed), so a just-launched agent
  // intentionally won't appear here. Pop a toast with a deep-link to the
  // agents view so the user can follow it without losing context.
  const handleLaunchSuccess = useCallback(
    (agent: Agent) => {
      void setSelectionParam(itemId({ type: 'agent', agent }), { history: 'replace' });
      toast.success('Agent launched', {
        description: "It won't appear here until it needs your input.",
        action: {
          label: 'View agent',
          onClick: () => navigate(routes.agent(agent.id)),
        },
      });
    },
    [navigate, routes, setSelectionParam],
  );

  const activities = useLiveAgentTurnSignals();
  const buckets: Buckets = useMemo(
    () => buildBuckets(agents, flowRuns, activities, gates),
    [agents, flowRuns, activities, gates],
  );
  const { notifications } = useNotifications();

  const archiveAgent = useArchiveAgent();
  const [confirmArchiveAgent, setConfirmArchiveAgent] = useState<Agent | null>(null);
  const [confirmDeleteAgent, setConfirmDeleteAgent] = useState<Agent | null>(null);

  const handleArchiveAgent = useCallback(
    (agent: Agent) => {
      // Archiving a still-working agent is easy to do by accident, so confirm
      // first. The archive itself is non-destructive: it only flips the flag,
      // so the run keeps going and can be unarchived later. The derived
      // activity is what decides: a `running` agent that is idle between turns,
      // waiting for input, or interrupted has no turn in flight, so it archives
      // straight away — which is the whole point of triaging from the inbox.
      // Derived from the durable turn state, the same rule the agent
      // sidebar's archive confirm uses.
      if (shouldConfirmArchive(agent, activityFromDurableAgentState(agent))) {
        setConfirmArchiveAgent(agent);
        return;
      }
      archiveAgent.mutate(agent.id);
    },
    [archiveAgent],
  );

  const handleConfirmArchiveActiveAgent = useCallback(() => {
    if (!confirmArchiveAgent) return;
    archiveAgent.mutate(confirmArchiveAgent.id);
    setConfirmArchiveAgent(null);
  }, [confirmArchiveAgent, archiveAgent]);

  // Delete removes the run and tears its sandbox down, so it asks first rather
  // than deleting on the click; `DeleteAgentDialog` owns the question and the
  // mutation.
  const handleDeleteAgent = useCallback((agent: Agent) => {
    setConfirmDeleteAgent(agent);
  }, []);

  const notificationItems: InboxItem[] = useMemo(
    () => notifications.map((notification) => ({ type: 'notification', notification })),
    [notifications],
  );

  // Order matters for fallback selection: agents/flows that need attention
  // win over notifications, and notifications are picked last so the user
  // still lands on an active task by default when both exist.
  const allItems = useMemo(
    () => [
      ...buckets.needsInput,
      ...buckets.idle,
      ...buckets.failed,
      ...buckets.completed,
      ...notificationItems,
    ],
    [buckets, notificationItems],
  );

  const selectionFromItem = useCallback((item: InboxItem): Selection => {
    switch (item.type) {
      case 'agent':
        return { type: 'agent', agent: item.agent };
      case 'decision-gate':
        return { type: 'decision-gate', gateId: item.gate.id };
      case 'flow-run':
        return { type: 'flow-run', runId: item.run.id };
      case 'notification':
        return { type: 'notification', notificationId: item.notification.id };
    }
  }, []);

  const resolvedSelection = useMemo<Selection | null>(() => {
    if (allItems.length === 0) return null;

    if (selectionParam) {
      const matchingItem = allItems.find((item) => itemId(item) === selectionParam);
      if (matchingItem) return selectionFromItem(matchingItem);
    }

    return selectionFromItem(allItems[0]);
  }, [allItems, selectionParam, selectionFromItem]);

  const handleSelect = useCallback(
    (item: InboxItem) => {
      void setSelectionParam(itemId(item), { history: 'replace' });
      panel.closeSidebar();
    },
    [panel, setSelectionParam],
  );

  const sidebarContent = (
    <InboxSidebar
      buckets={buckets}
      selection={resolvedSelection}
      onSelect={handleSelect}
      onArchiveAgent={handleArchiveAgent}
      onDeleteAgent={handleDeleteAgent}
      onLaunchAgent={showLaunchButton ? handleOpenLaunchDialog : undefined}
      takeover={takeover}
    />
  );

  const mobileSidebarContent = (
    <InboxSidebar
      buckets={buckets}
      selection={resolvedSelection}
      onSelect={handleSelect}
      onArchiveAgent={handleArchiveAgent}
      onDeleteAgent={handleDeleteAgent}
      onLaunchAgent={showLaunchButton ? handleOpenLaunchDialog : undefined}
      onToggleSidebar={panel.closeSidebar}
    />
  );

  return (
    <>
      <TwoPanelLayout
        autoSaveId={autoSaveId}
        defaultSidebarWidth={288}
        sidebar={sidebarContent}
        mobileSidebar={mobileSidebarContent}
        sidebarOpen={panel.sidebarOpen}
        onSidebarOpenChange={panel.setSidebarOpen}
      >
        <AnimatePresence mode="wait" initial={false}>
          {resolvedSelection ? (
            <motion.div
              key={selectionKey(resolvedSelection)}
              className="flex h-full flex-col"
              {...fadeInUp}
            >
              {resolvedSelection.type === 'agent' ? (
                renderAgentDetail ? (
                  renderAgentDetail(resolvedSelection.agent, panel.toggleSidebar)
                ) : (
                  <AgentStreamView
                    key={resolvedSelection.agent.id}
                    agent={resolvedSelection.agent}
                    onToggleSidebar={panel.toggleSidebar}
                  />
                )
              ) : resolvedSelection.type === 'decision-gate' ? (
                (() => {
                  const gate = gates.find((g) => g.id === resolvedSelection.gateId);
                  if (!gate) return <EmptyInbox onToggleSidebar={panel.toggleSidebar} />;
                  if (renderDecisionGateDetail) {
                    return renderDecisionGateDetail(gate, panel.toggleSidebar);
                  }
                  return (
                    <DecisionGateDetail
                      key={gate.id}
                      gate={gate}
                      onToggleSidebar={panel.toggleSidebar}
                    />
                  );
                })()
              ) : resolvedSelection.type === 'flow-run' ? (
                renderFlowRunBranch({
                  runId: resolvedSelection.runId,
                  flowRuns,
                  renderFlowRunDetail,
                  onToggleSidebar: panel.toggleSidebar,
                })
              ) : (
                <NotificationDetailPanel
                  key={resolvedSelection.notificationId}
                  notificationId={resolvedSelection.notificationId}
                  onToggleSidebar={panel.toggleSidebar}
                />
              )}
            </motion.div>
          ) : (
            <motion.div key="empty" className="flex h-full flex-col" {...fadeInUp}>
              <EmptyInbox
                onToggleSidebar={panel.toggleSidebar}
                onLaunchAgent={showLaunchButton ? handleOpenLaunchDialog : undefined}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </TwoPanelLayout>

      {showLaunchButton && (
        <LaunchAgentDialog
          open={launchDialogOpen}
          onOpenChange={setLaunchDialogOpen}
          onSuccess={handleLaunchSuccess}
        />
      )}

      <ConfirmDialog
        open={!!confirmArchiveAgent}
        onOpenChange={(open) => {
          if (!open) setConfirmArchiveAgent(null);
        }}
        title="Archive this running agent?"
        description="This agent is still working. Archiving won't stop it; the run keeps going and you can unarchive it anytime."
        confirmLabel="Archive"
        onConfirm={handleConfirmArchiveActiveAgent}
      />

      {confirmDeleteAgent && (
        <DeleteAgentDialog
          agent={confirmDeleteAgent}
          open
          onOpenChange={(open) => {
            if (!open) setConfirmDeleteAgent(null);
          }}
        />
      )}
    </>
  );
}
